import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Download, Smartphone, Monitor, CheckCircle2, Share, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const InstallPage = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent;
    setIsIOS(/iPad|iPhone|iPod/.test(ua));

    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setIsInstalled(true));

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setIsInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full space-y-8 text-center"
      >
        <img src="/pwa-icon-512.png" alt="ViaMenu" className="w-24 h-24 mx-auto rounded-2xl shadow-lg" />

        <div>
          <h1 className="text-2xl font-bold text-foreground">Instalar ViaMenu</h1>
          <p className="text-muted-foreground text-sm mt-2">
            Acesse seu cardápio e dashboard como um app direto na tela inicial.
          </p>
        </div>

        {isInstalled ? (
          <div className="flex flex-col items-center gap-3 p-6 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <p className="text-sm font-medium text-emerald-600">App já instalado!</p>
          </div>
        ) : isIOS ? (
          <div className="space-y-4 p-6 rounded-xl bg-card border border-border text-left">
            <p className="text-sm font-semibold text-foreground">No iPhone/iPad:</p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Share className="h-5 w-5 text-primary shrink-0" />
                <span>Toque no botão <strong>Compartilhar</strong> no Safari</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Download className="h-5 w-5 text-primary shrink-0" />
                <span>Selecione <strong>"Adicionar à Tela de Início"</strong></span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                <span>Confirme tocando em <strong>"Adicionar"</strong></span>
              </div>
            </div>
          </div>
        ) : deferredPrompt ? (
          <Button onClick={handleInstall} size="lg" className="w-full gap-2">
            <Download className="h-5 w-5" />
            Instalar App
          </Button>
        ) : (
          <div className="space-y-4 p-6 rounded-xl bg-card border border-border text-left">
            <p className="text-sm font-semibold text-foreground">No Android/Desktop:</p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <MoreVertical className="h-5 w-5 text-primary shrink-0" />
                <span>Abra o <strong>menu do navegador</strong> (⋮)</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Download className="h-5 w-5 text-primary shrink-0" />
                <span>Selecione <strong>"Instalar app"</strong> ou <strong>"Adicionar à tela inicial"</strong></span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 pt-4">
          <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-card border border-border">
            <Smartphone className="h-6 w-6 text-primary" />
            <span className="text-xs text-muted-foreground">Celular</span>
          </div>
          <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-card border border-border">
            <Monitor className="h-6 w-6 text-primary" />
            <span className="text-xs text-muted-foreground">Desktop</span>
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground">
          Funciona offline • Atualizações automáticas • Sem ocupar espaço
        </p>
      </motion.div>
    </div>
  );
};

export default InstallPage;
